import {useGenerateBenefitPeriodsMutation} from '@transactions/graphql';
import {fromSeconds, today} from 'common/date';
import {Plus, Settings2, Sparkles, X} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import {TextInput} from 'src/inputs/TextInput';
import type {DateTime} from 'luxon';

export type PeriodRow = {
  startDate: DateTime;
  endDate: DateTime;
  amount: string; // dollar string
};

type Props = {
  periods: PeriodRow[];
  onChange: (periods: PeriodRow[]) => void;
  // When false (and there is exactly one period), show a simple amount-only
  // view; the full period editor is revealed by "Customize periods"
  defaultCustomize: boolean;
  defaultGenStartDate: DateTime;
  onError: (message: string | null) => void;
};

const formatDate = (date: DateTime): string =>
  date.toLocaleString({month: 'short', day: 'numeric', year: 'numeric'});

export const PeriodsEditor = ({
  periods,
  onChange,
  defaultCustomize,
  defaultGenStartDate,
  onError,
}: Props) => {
  const [customize, setCustomize] = React.useState(defaultCustomize);

  // AI generation state
  const [showGenerate, setShowGenerate] = React.useState(false);
  const [genDescription, setGenDescription] = React.useState('');
  const [genStartDate, setGenStartDate] =
    React.useState<DateTime>(defaultGenStartDate);
  const [genYears, setGenYears] = React.useState('1');

  const [generatePeriods, {loading: generating}] =
    useGenerateBenefitPeriodsMutation();

  const handleAddPeriod = () => {
    onChange([...periods, {startDate: today(), endDate: today(), amount: ''}]);
  };

  const handleRemovePeriod = (index: number) => {
    onChange(periods.filter((_, i) => i !== index));
  };

  const handlePeriodDateChange = (
    index: number,
    field: 'startDate' | 'endDate',
    value: DateTime,
  ) => {
    const updated = [...periods];
    updated[index] = {...updated[index], [field]: value};
    onChange(updated);
  };

  const handlePeriodAmountChange = (index: number, value: string) => {
    const updated = [...periods];
    updated[index] = {...updated[index], amount: value};
    onChange(updated);
  };

  const handleGenerate = async () => {
    onError(null);
    try {
      if (!genStartDate.isValid) throw new Error('Invalid start date');
      const years = parseInt(genYears, 10);
      if (isNaN(years) || years < 1) throw new Error('Years must be at least 1');

      const result = await generatePeriods({
        variables: {
          description: genDescription,
          startDate: genStartDate.toISODate(),
          years,
        },
      });

      if (result.data) {
        const generated = result.data.generateBenefitPeriods.map((p) => ({
          startDate: fromSeconds(p.startDate),
          endDate: fromSeconds(p.endDate),
          amount: (p.amount / 100).toFixed(2),
        }));
        onChange(generated);
        setShowGenerate(false);
      }
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      onError(err.message);
    }
  };

  // Simple view is only meaningful for a single period; multi-period benefits
  // always show the full editor
  if (!customize && periods.length === 1) {
    const period = periods[0];
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Amount</label>
          <Button
            variant="ghost"
            type="button"
            className="text-muted-foreground text-sm"
            onClick={() => setCustomize(true)}
          >
            <Settings2 className="mr-1 h-4 w-4" />
            Customize periods
          </Button>
        </div>
        <div className="relative">
          <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
            $
          </span>
          <TextInput
            value={period.amount}
            onChange={(v) => handlePeriodAmountChange(0, v)}
            placeholder="0.00"
            className="pl-7"
          />
        </div>
        <p className="text-muted-foreground text-xs">
          For {formatDate(period.startDate)} &ndash; {formatDate(period.endDate)}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-sm font-medium">Periods</label>
        <div className="flex gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={() => setShowGenerate(!showGenerate)}
          >
            <Sparkles className="mr-1 h-4 w-4" />
            Generate with AI
          </Button>
          <Button variant="outline" type="button" onClick={handleAddPeriod}>
            <Plus className="mr-1 h-4 w-4" />
            Add period
          </Button>
        </div>
      </div>

      {showGenerate && (
        <div className="border-border bg-muted/50 flex flex-col gap-2 rounded-md border p-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium">Description</label>
            <TextInput
              value={genDescription}
              onChange={setGenDescription}
              placeholder='e.g., "$10/month Uber credit, resets on the 1st"'
            />
          </div>
          <div className="flex gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-medium">Start date</label>
              <DatePicker selectedDate={genStartDate} onChange={setGenStartDate} />
            </div>
            <div className="flex w-24 flex-col gap-1">
              <label className="text-xs font-medium">Years</label>
              <TextInput
                type="number"
                value={genYears}
                onChange={setGenYears}
                min="1"
              />
            </div>
          </div>
          <Button
            variant="primary"
            type="button"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleGenerate}
            disabled={generating || !genDescription.trim()}
          >
            {generating ? 'Generating...' : 'Generate'}
          </Button>
        </div>
      )}

      {periods.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No periods added yet. Add manually or generate with AI.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {periods.map((period, index) => (
            // flex-wrap lets the amount drop to its own full-width line on
            // narrow screens instead of getting crushed next to the dates
            <div key={index} className="flex flex-wrap items-center gap-2">
              <div className="flex shrink-0 items-center gap-2">
                <div className="w-32">
                  <DatePicker
                    selectedDate={period.startDate}
                    onChange={(v) => handlePeriodDateChange(index, 'startDate', v)}
                  />
                </div>
                <span className="text-muted-foreground text-sm">&ndash;</span>
                <div className="w-32">
                  <DatePicker
                    selectedDate={period.endDate}
                    onChange={(v) => handlePeriodDateChange(index, 'endDate', v)}
                  />
                </div>
              </div>
              <div className="flex min-w-36 flex-1 items-center gap-2">
                <div className="relative flex-1">
                  <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
                    $
                  </span>
                  <TextInput
                    value={period.amount}
                    onChange={(v) => handlePeriodAmountChange(index, v)}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  type="button"
                  onClick={() => handleRemovePeriod(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
